<script>
import {useI18n} from "vue-i18n";
import Icon from "@/javascript/views/components/Icon.vue";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import {usePaymentProcessorStore} from "../../../stores/paymentProcessors";
import Tooltip from "../../components/tooltip.vue";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";

export default {
  components: {Tooltip, LoadingSpinner, Icon},
  props: {
    paymentProcessor: {
      type: [Object, null],
      required: true
    },
    processorType: {
      type: String,
      required: true,
    },
    showIntervals: {
      type: Boolean,
      default: true
    },
    showSuccessUrl: {
      type: Boolean,
      default: true
    },
    showOrganizationName: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    // I18n
    const {t} = useI18n();

    const processorStore = usePaymentProcessorStore();
    const analyticEventStore = useAnalyticEventStore();
    return {
      t,
      processorStore,
      analyticEventStore
    };
  },
  data() {
    return {
      validIntervals: ["Weekly", "Biweekly", "Monthly", "Quarterly", "Yearly"],
      apiKey: '',
      apiSecret: '',
      showCreateModal: false,
      processorCreateType: "",
      showDetailsModal: false,
      showOfflineModal: false,
      errors: {},
      processorDetails: {},
      dirty: false
    };
  },
  computed: {
    loading() {
      return this.processorStore.loading;
    },

    // Checks for differences in temp processor data to toggle save button on/ off
    saveButtonEnabled() {
      return this.dirty ||
        this.paymentProcessor.success_url !== this.processorDetails.success_url ||
        this.processorDetails.active !== this.paymentProcessor.active ||
        this.processorDetails.delayed_subscriptions_enabled !== this.paymentProcessor.delayed_subscriptions_enabled;
    }
  },
  watch: {
    apiKey() {
      this.errors["key"] = "";
    },
    apiSecret() {
      this.errors["secret"] = "";
    }
  },
  methods: {
    isIntervalEnabled(interval, editing) {
      if (editing) {
        return this.processorDetails.enabled_subscription_intervals.includes(interval.toLowerCase());
      }
      return this.paymentProcessor.enabled_subscription_intervals.includes(interval.toLowerCase());
    },
    // Open / Close Modals
    openCreateModal(processorType) {
      this.processorCreateType = processorType;
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;

      this.apiKey = "";
      this.apiSecret = "";
      this.processorStore.resetConfig();
    },
    openDetailsModal() {
      this.populateEditForm();
      this.showDetailsModal = true;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.processorStore.resetConfig();
    },
    // Populate the Edit form with temp data
    populateEditForm() {
      this.processorDetails = JSON.parse(JSON.stringify(this.paymentProcessor));
    },

    // Toggle active subscription intervals on/ off
    toggleInterval(interval) {
      this.dirty = true;
      this.errors['intervals'] = null;
      let i = this.processorDetails.enabled_subscription_intervals.indexOf(interval.toLowerCase());
      if (i !== -1) {
        this.processorDetails.enabled_subscription_intervals.splice(i, 1);
      } else {
        this.processorDetails.enabled_subscription_intervals.push(interval.toLowerCase());
      }
    },

    // Save Edit form changes
    async saveEditForm() {
      if (this.validateEditForm()) {
        await this.processorStore.updateProcessor(this.processorDetails);
        if (this.processorStore.editModal.success) {
          this.analyticEventStore.logEvent({
            action: `payment-processor-modal-success`,
            traceable_type: "Processor::PaymentProcessor",
            traceable_id: this.processorDetails.id,
            metadata: {
              data: this.processorDetails
            }
          });
          setTimeout(() => {
            this.closeDetailsModal();
          }, 1200);
        }
      }
    },

    // Set up Stripe account with credentials
    async linkProcessorAccount() {
      if (this.validateConfigForm()) {
        if (this.processorCreateType === 'STRIPE') {
          await this.processorStore.configureStripe(this.apiKey, this.apiSecret);
        }
        if (this.processorCreateType === 'HELCIM') {
          await this.processorStore.configureHelcim(this.apiKey);

        }
        if (this.processorStore.processorConfig.success) {
          setTimeout(() => {
            this.closeCreateModal();
          }, 2000);
        }
      }
    },

    validateConfigForm() {
      // Very basic validation of API Keys
      let valid = true;
      if (!this.apiKey || this.apiKey.length < 20) {
        this.errors['key'] = "Enter a valid key";
        valid = false;
      }
      if ((!this.apiSecret || this.apiSecret.length < 20) && this.processorCreateType === 'STRIPE') {
        this.errors['secret'] = "Enter a valid key";
        valid = false;
      }
      return valid;
    },

    validateEditForm() {
      let valid = true;
      if (this.processorDetails.enabled_subscription_intervals.length === 0) {
        this.errors['intervals'] = "At least one interval should be enabled";
        valid = false;
      }
      if (this.processorDetails.success_url) {
        try {
          const url = new URL(this.processorDetails.success_url);
          if (!url) {
            this.errors['url'] = "Enter a valid URL";
            valid = false;
          }
        } catch {
          this.errors['url'] = "Enter a valid URL";
          valid = false;
        }
      }
      return valid;
    },

    // Format money from backend, cents to dollars
    formatMoney(cents) {

      const amountInCents = parseInt(cents, 10);
      const dollars = amountInCents / 100.0;
      const formattedAmount = dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `$${formattedAmount}`;
    }

  },
};
</script>

<template>
  <!-- Main Card -->
  <div class="overflow-hidden">
    <div>
      <div
        class="payment-processor-card"
        :class="{'view-only': paymentProcessor?.viewOnly}"
      >
        <div>
          <div v-if="loading">
            <div class="w-20 h-8 bg-gray-300 rounded animate-pulse" />
          </div>
          <div
            v-else
            class="flex flex-row justify-between"
          >
            <div class="flex flex-row">
              <!-- If the processor is set up -->
              <div v-if="processorType === 'STRIPE'">
                <img
                  src="../../../../assets/logos/stripe.svg"
                  style="height: 24px;"
                  alt="Stripe Logo"
                >
              </div>
              <div v-else-if="processorType === 'HELCIM'">
                <img
                  src="../../../../assets/logos/helcim_logo.svg"
                  style="height: 24px;"
                  alt="Helcim Logo"
                >
              </div>
              <div v-else>
                <p class="text-lg font-semibold">
                  {{ processorType }}
                </p>
              </div>
              <div
                v-if="paymentProcessor && paymentProcessor.active"
                class="rounded-sm bg-default_secondary text-xs font-bold text-white ml-1 p-1 h-6"
              >
                ACTIVE
              </div>
            </div>
            <div
              v-if="paymentProcessor && processorType !== 'OFFLINE' && !paymentProcessor.viewOnly"
              class="cursor-pointer"
              @click="openDetailsModal"
            >
              <icon
                icon="gear"
              />
            </div>

            <div
              v-if="showOrganizationName"
              class="font-semibold"
            >
              {{ paymentProcessor.organization_name }}
            </div>
          </div>
          <div class="border-t border-gray-300 my-3" />
          <div v-if="loading">
            <div class="w-100 h-10 bg-gray-300 rounded animate-pulse" />
            <div class="w-100 h-20 bg-gray-300 rounded animate-pulse my-2" />
            <div class="w-100 h-10 bg-gray-300 rounded animate-pulse" />
          </div>

          <div
            v-else-if="paymentProcessor"
            class="flex flex-col"
          >
            <div
              v-if="!paymentProcessor?.viewOnly"
              class=""
            >
              <p class="text-xs font-semibold">
                Dollars YTD
              </p>
              <p class="text-lg font-semibold">
                {{ formatMoney(paymentProcessor.donations_ytd) }}
              </p>
            </div>
            <div
              v-show="showIntervals"
              class="flex flex-1 flex-grow-1 items-center"
            >
              <div
                v-if="!paymentProcessor?.viewOnly"
                class="border-t border-gray-300 my-2"
              />
              <div>
                <p class="text-xs font-semibold">
                  Intervals
                </p>
                <div class="flex flex-row flex-wrap justify-start gap-2">
                  <div
                    v-for="interval in validIntervals"
                    :key="interval"
                    class="inline-block max-w-max"
                  >
                    <div
                      v-if="isIntervalEnabled(interval, false)"
                      class="rounded border border-gray-300 p-1 bg-violet-500 text-xs text-white inline-block justify-start"
                    >
                      {{ interval }}
                    </div>
                    <div
                      v-else
                      class="rounded border border-violet-600 p-1 bg-pink text-xs"
                    >
                      {{ interval }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-show="showSuccessUrl && !paymentProcessor?.viewOnly">
              <div class="border-t border-gray-300 my-3" />
              <div class="text-xs">
                <p class="font-semibold">
                  Success URL
                </p>
                <p v-if="paymentProcessor.success_url">
                  {{ paymentProcessor.success_url }}
                </p>
                <p v-else>
                  Not Set
                </p>
              </div>
            </div>
          </div>
          <div
            v-else
            class="flex flex-col justify-center items-center cursor-pointer"
            @click="openCreateModal(processorType)"
          >
            <span class="mb-3 text-xs font-bold text-center">Configure {{ processorType }}</span>
            <icon
              icon="plus"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Set up Modal -->
  <div
    v-if="showCreateModal"
    class="fixed inset-0 flex items-center justify-center z-50"
  >
    <div class="fixed inset-0 bg-black opacity-50" />

    <div class="relative bg-white p-6 max-w-sm mx-auto rounded-lg shadow-lg transition-all transform">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-bold">
          Configure your {{ processorCreateType }} Account
        </h2>
        <button
          class="text-gray-500 hover:text-gray-700 focus:outline-none"
          @click="closeCreateModal"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Modal body -->
      <div
        v-if="processorStore.processorConfig.loading"
        class="flex flex-col justify-center items-center my-20"
      >
        <loading-spinner size="small" />
        <div class="text-sm mt-2">
          {{ processorStore.processorConfig.loadingMessage }}
        </div>
      </div>
      <div
        v-else-if="processorStore.processorConfig.success"
        class="flex flex-col justify-center items-center my-20"
      >
        <div
          class="text-blue-500 text-lg"
        >
          Account Linked!
        </div>
      </div>
      <div v-else>
        <p
          v-if="processorCreateType === 'STRIPE'"
          class="text-sm font-semibold mb-3"
        >
          Link your Stripe account to start collecting donations
        </p>
        <p
          v-if="processorCreateType === 'Helcim'"
          class="text-sm font-semibold mb-3"
        >
          Link your Helcim account to start collecting donations
        </p>
        <div>
          <label
            for="api_key"
            class="block text-sm font-medium text-gray-700"
          >
            {{ processorCreateType === "STRIPE" ? 'Publishable Key' : 'API key' }}
          </label>
          <input
            id="api_key"
            v-model="apiKey"
            type="password"
            placeholder="Enter Key"
            class="mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
          <span
            v-if="errors['key']"
            class="text-red-500 text-xs"
          > {{ errors['key'] }}</span>
          <label
            v-if="processorCreateType === 'STRIPE'"
            for="api_secret"
            class="block mt-4 text-sm font-medium text-gray-700"
          >Secret Key</label>
          <input
            v-if="processorCreateType === 'STRIPE'"
            id="api_secret"
            v-model="apiSecret"
            type="password"
            placeholder="Enter key"
            class="mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
          <span
            v-if="errors['secret']"
            class="text-red-500 text-xs"
          > {{ errors['secret'] }}</span>
          <span
            v-if="processorStore.processorConfig.error"
            class="text-red-500 text-xs"
          > {{ processorStore.processorConfig.message }}</span>
        </div>
        <button
          class="button-filled px-4 py-2 rounded mt-3"
          @click="linkProcessorAccount"
        >
          Link Account
        </button>
      </div>
    </div>
  </div>

  <!-- Details Modal -->
  <div
    v-if="showDetailsModal"
    class="fixed inset-0 flex items-center justify-center z-50"
  >
    <div class="fixed inset-0 bg-black opacity-50" />

    <div class="relative bg-white p-6 max-w-sm mx-auto rounded-lg shadow-lg transition-all transform">
      <div class="flex justify-between items-center mb-4">
        <!-- If the processor is set up -->
        <div v-if="processorType === 'STRIPE'">
          <img
            src="../../../../assets/logos/stripe.svg"
            style="height: 40px;"
            alt="Stripe Logo"
          >
        </div>
        <div v-else-if="processorType === 'HELCIM'">
          <img
            src="../../../../assets/logos/helcim_logo.svg"
            style="height: 40px;"
            alt="Helcim Logo"
          >
        </div>
        <div v-else>
          <p class="text-lg font-semibold">
            {{ processorType }}
          </p>
        </div>
        <button
          class="text-gray-500 hover:text-gray-700 focus:outline-none"
          @click="closeDetailsModal"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="border-t border-gray-300 my-2" />

      <!-- Modal body -->
      <div
        v-if="processorStore.editModal.loading"
        class="flex flex-col justify-center items-center my-5 mx-20"
      >
        <loading-spinner size="small" />
        <div class="text-sm mt-2">
          SAVING
        </div>
      </div>
      <div
        v-else-if="processorStore.editModal.success"
        class="flex flex-col justify-center items-center my-5 mx-20"
      >
        <div
          class="text-blue-500 text-lg"
        >
          Saved!
        </div>
      </div>
      <div
        v-else
      >
        <p class="text-sm font-semibold mb-1">
          Edit Payment Processor
        </p>
        <div>
          <div class="flex flex-row mb-1">
            <input
              v-model="processorDetails.active"
              type="checkbox"
            >
            <div class="ml-1 mr-3">
              Active
            </div>
            <tooltip
              message="Each organization can have only 1 active online payment processor.
              Checking this box will make this your organization’s active payment processor and deactivate the current one.
              Any other previously configured online payment processors will remain attached to your organization but will be inactive while another is chosen.
              Changing your active payment processor will not affect any existing recurring gifts."
              variant="question"
              size="12px"
            />
          </div>

          <div class="flex flex-row mb-1">
            <input
              v-model="processorDetails.delayed_subscriptions_enabled"
              type="checkbox"
            >
            <div class="ml-1 mr-3">
              Allow Delayed Subscriptions
            </div>
            <tooltip
              message="Allow donors to set a date in the future to start a subscription gift. "
              variant="question"
              size="12px"
            />
          </div>

          <p class="text-xs font-semibold">
            Subscription Intervals
          </p>
          <span
            v-if="errors['intervals']"
            class="text-red-500 text-xs"
          > {{ errors['intervals'] }}</span>
          <div class="flex flex-row flex-wrap justify-start">
            <div
              v-for="interval in validIntervals"
              :key="interval"
              class="inline-block max-w-max mr-2"
            >
              <div
                class="rounded border border-gray-300 p-1 text-xs cursor-pointer inline-block justify-start "
                :class="[{ 'border-violet-600': !isIntervalEnabled(interval, true),
                           'bg-violet-500 text-white ': isIntervalEnabled(interval, true) }]"

                @click="toggleInterval(interval)"
              >
                {{ interval }}
              </div>
            </div>
          </div>
          <label
            for="success_url"
            class="block text-xs font-semibold mt-4"
          >Success URL</label>
          <input
            id="success_url"
            v-model="processorDetails.success_url"
            placeholder="Enter URL.."
            class="mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            @input="errors['url']=null"
          >
          <span
            v-if="errors['url']"
            class="text-red-500 text-xs"
          > {{ errors['url'] }}</span>
        </div>
        <div class="mt-4 flex flex-row">
          <button
            class="mt-3 mr-2 border btn button-filled"
            :disabled="!saveButtonEnabled"
            :class="{'cursor-loading': !saveButtonEnabled}"
            @click="saveEditForm"
          >
            Save
          </button>
          <button
            class="btn-outline mt-3"
            @click="closeDetailsModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.payment-processor-card {
  @apply rounded-lg border border-gray-300 bg-white px-4 pt-4 pb-4  min-h-48;

  &.view-only {
    @apply bg-gray-50;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.btn-filled:disabled {
  @apply bg-gray-400;
  border: none;
}
</style>
